import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { ApiResponse, PageableDto } from '@shared-models';
import { API_CONFIG, ApiConfig } from '../../api-config';
import { mapMutationMessage } from '../../../utils/map-pageable';

/** Shape API de un slot del checklist / documento de empresa. */
export interface CompanyDocumentDto {
  id?: string | null;
  companyId?: string;
  countryDocumentId?: string;
  typeDocumentId?: string;
  typeDocumentPrefix?: string;
  typeDocumentCode?: string;
  typeDocumentName?: string;
  name: string;
  required?: boolean;
  fileUrl?: string | null;
  originalName?: string | null;
  contentType?: string;
  fileSize?: number;
  validityDate?: string | null;
  noExpiration?: boolean;
  status?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export type CompanyDocumentWritePayload = {
  countryDocumentId: string;
  validityDate?: string | null;
  noExpiration?: boolean;
};

export type CompanyDocumentListParams = {
  page?: number;
  size?: number;
  sort?: string;
  direction?: 'asc' | 'desc';
  status?: string;
  search?: string;
};

/**
 * Documentos de empresa (checklist por documentos del país).
 *   GET    /api/structure/companies/{companyId}/documents
 *   POST   /api/structure/companies/{companyId}/documents/upsert
 *   PUT    /api/structure/companies/{companyId}/documents/{id}
 *   PATCH  /api/structure/companies/{companyId}/documents/{id}/status
 *   DELETE /api/structure/companies/{companyId}/documents/{id}
 */
@Injectable({ providedIn: 'root' })
export class CompanyDocumentService {
  private readonly http = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private resource(companyId: string): string {
    return `${this.apiConfig.apiUrl}/structure/companies/${companyId}/documents`;
  }

  list(
    companyId: string,
    params: CompanyDocumentListParams = {},
  ): Observable<CompanyDocumentDto[]> {
    let httpParams = new HttpParams();
    if (params.status?.trim()) {
      httpParams = httpParams.set('status', params.status.trim());
    }
    if (params.search?.trim()) {
      httpParams = httpParams.set('search', params.search.trim());
    }

    return this.http
      .get<
        ApiResponse<CompanyDocumentDto[] | PageableDto<CompanyDocumentDto>>
      >(this.resource(companyId), { params: httpParams })
      .pipe(map((res) => this.unwrapList(res.data)));
  }

  /** Upsert por countryDocumentId (crea o actualiza). */
  upsert(
    companyId: string,
    payload: CompanyDocumentWritePayload,
    file?: File | null,
  ): Observable<{ message: string; data?: CompanyDocumentDto }> {
    return this.http
      .post<{
        success?: boolean;
        message?: string;
        data?: CompanyDocumentDto;
      }>(`${this.resource(companyId)}/upsert`, this.toFormData(payload, file))
      .pipe(
        map((response) => ({
          ...mapMutationMessage(response, 'Documento guardado'),
          data: response.data,
        })),
      );
  }

  create(
    companyId: string,
    payload: CompanyDocumentWritePayload,
    file: File,
  ): Observable<{ message: string }> {
    return this.http
      .post<{ success?: boolean; message?: string }>(
        this.resource(companyId),
        this.toFormData(payload, file),
      )
      .pipe(
        map((response) => mapMutationMessage(response, 'Documento creado')),
      );
  }

  update(
    companyId: string,
    documentId: string,
    payload: Partial<CompanyDocumentWritePayload>,
    file?: File | null,
  ): Observable<{ message: string }> {
    return this.http
      .put<{ success?: boolean; message?: string }>(
        `${this.resource(companyId)}/${documentId}`,
        this.toFormData(
          {
            countryDocumentId: payload.countryDocumentId ?? '',
            validityDate: payload.validityDate,
            noExpiration: payload.noExpiration,
          },
          file,
        ),
      )
      .pipe(
        map((response) =>
          mapMutationMessage(response, 'Documento actualizado'),
        ),
      );
  }

  updateStatus(
    companyId: string,
    documentId: string,
    status: 'active' | 'inactive',
  ): Observable<{ message: string }> {
    const apiStatus = status === 'inactive' ? 'INACTIVE' : 'ACTIVE';
    return this.http
      .patch<{ success?: boolean; message?: string }>(
        `${this.resource(companyId)}/${documentId}/status`,
        { status: apiStatus },
      )
      .pipe(
        map((response) =>
          mapMutationMessage(response, 'Estatus actualizado exitosamente'),
        ),
      );
  }

  delete(
    companyId: string,
    documentId: string,
  ): Observable<{ message: string }> {
    return this.http
      .delete<{ success?: boolean; message?: string }>(
        `${this.resource(companyId)}/${documentId}`,
      )
      .pipe(
        map((response) =>
          mapMutationMessage(response, 'Documento eliminado exitosamente'),
        ),
      );
  }

  private toFormData(
    payload: CompanyDocumentWritePayload,
    file?: File | null,
  ): FormData {
    const formData = new FormData();
    if (payload.countryDocumentId) {
      formData.append('countryDocumentId', payload.countryDocumentId);
    }
    if (payload.noExpiration != null) {
      formData.append('noExpiration', String(payload.noExpiration));
    }
    if (payload.validityDate) {
      formData.append('validityDate', payload.validityDate);
    }
    if (file) {
      formData.append('file', file);
    }
    return formData;
  }

  private unwrapList(
    data:
      | CompanyDocumentDto[]
      | PageableDto<CompanyDocumentDto>
      | null
      | undefined,
  ): CompanyDocumentDto[] {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.content)) return data.content;
    return [];
  }
}
